import { Children, isValidElement, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import "highlight.js/styles/github-dark.css";
import { Box } from "@mantine/core";
import { Link } from "@inertiajs/react";
import { Callout } from "./mdx/Callout";
import { Quote } from "./mdx/Quote";
import { VideoEmbed } from "./mdx/VideoEmbed";
import { PostImage } from "./mdx/PostImage";
import { SoundcloudEmbed } from "./mdx/SoundcloudEmbed";
import { GenericEmbed } from "./mdx/GenericEmbed";

const INTERNAL_HOSTS = new Set(["learntokiz.io", "www.learntokiz.io"]);

function isInternalHref(href: string): boolean {
  if (!href) return true;
  if (href.startsWith("#")) return true;                              // anchor
  if (href.startsWith("mailto:") || href.startsWith("tel:")) return true;
  if (href.startsWith("/") && !href.startsWith("//")) return true;    // relative path
  try {
    const url = new URL(href, typeof window !== "undefined" ? window.location.origin : "https://learntokiz.io");
    if (typeof window !== "undefined" && url.origin === window.location.origin) return true;
    return INTERNAL_HOSTS.has(url.hostname);
  } catch {
    return true; // malformed URL, treat as internal to be safe
  }
}

interface Props {
  source: string;
}

// Custom MDX tags that render block-level elements (figure, div, iframe, aside).
// Remark treats unknown HTML tags as inline and wraps them in <p>, which is
// invalid nesting. The <p> renderer below unwraps when it detects one of these.
const BLOCK_MDX_TAGS = new Set(["callout", "quote", "videoembed", "postimage", "soundcloudembed", "embed"]);

const childIsBlockMdx = (child: ReactNode): boolean => {
  if (!isValidElement(child)) return false;
  const tagName = (child.props as { node?: { tagName?: string } })?.node
    ?.tagName;
  return typeof tagName === "string" && BLOCK_MDX_TAGS.has(tagName);
};

const components = {
  callout: Callout,
  quote: Quote,
  videoembed: VideoEmbed,
  postimage: PostImage,
  soundcloudembed: SoundcloudEmbed,
  embed: GenericEmbed,
  p: ({ children, ...props }) => {
    const kids = Children.toArray(children);
    if (kids.some(childIsBlockMdx)) {
      return <>{children}</>;
    }
    return <p {...props}>{children}</p>;
  },
  a: ({ href, children, ...props }) => {
    const url = href ?? "";
    if (!isInternalHref(url)) {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    }
    // Anchor jumps and mailto/tel stay as plain anchors
    if (url.startsWith("#") || url.startsWith("mailto:") || url.startsWith("tel:")) {
      return <a href={url} {...props}>{children}</a>;
    }
    // Same-origin route → Inertia SPA nav
    return <Link href={url} {...props}>{children}</Link>;
  },
} as unknown as Components;

export function MarkdownRenderer({ source }: Props) {
  return (
    <Box
      className="markdown-body"
      style={{
        color: "var(--ink-body)",
        lineHeight: 1.7,
        fontSize: 16,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSlug, rehypeHighlight]}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
}

export default MarkdownRenderer;
